import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Package, Leaf, Calculator, Truck, Clock, BadgeCheck } from "lucide-react";
import axios from "axios";
import { useSEO } from "../hooks/useSEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const HERO_IMG = "/images/Re_colte-3.webp";
const FARMER_IMG = "/images/Exploitation-portrait.jpg";

// Abréviations affichées sous chaque case. "Juin"/"Juil." plutôt que "Jui"/"Jul",
// qui se confondaient au premier coup d'œil.
const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil.", "Août", "Sep", "Oct", "Nov", "Déc"];
const monthsFull = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const seasonality = {
  rhizomes: [false, true, true, true, false, false, false, false, false, false, false, false],
  paille: [true, true, true, true, true, true, true, true, true, true, true, true],
  biomasse: [true, true, true, false, false, false, false, false, false, true, true, true],
};

const products = [
  {
    id: "rhizomes",
    name: "Rhizomes de Miscanthus",
    desc: "Rhizomes sélectionnés pour une reprise optimale. Planteuse à pomme de terre mise à disposition pour faciliter la mise en place.",
    img: "/images/Rhizome-1.webp",
    imgPosition: "center 65%",
    href: "/produits",
  },
  {
    id: "paille",
    name: "Paille de Miscanthus",
    desc: "Paillage durable et écologique pour jardins, potagers, vignes et élevages.",
    img: "/images/Paille-vrac.jpeg",
    imgPosition: "center",
    href: "/produits",
  },
  {
    id: "sachet",
    name: "Sachet 120L",
    desc: "Format pratique pour particuliers et petites surfaces.",
    img: "/images/Sachet-4.webp",
    imgPosition: "center 20%",
    href: "/produits",
  },
];

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);

  useSEO({
    title: "Miscanthus Sarthe — Rhizomes et paille de miscanthus | EARL Torcé Biomasse Energie",
    description: "Producteur de miscanthus en Sarthe : rhizomes, paille en vrac et sachets 120L. Vente directe de l'exploitation, livraison en Pays de la Loire. Devis gratuit sous 48h.",
    path: "/",
  });

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(r => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative flex items-center justify-center"
        style={{ minHeight: "100vh", paddingTop: "4rem" }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto animate-fade-in">
          <span className="inline-block px-3 py-1 text-xs font-body font-500 uppercase tracking-widest rounded-full mb-6"
            style={{ backgroundColor: "rgba(181,122,62,0.9)", color: "white" }}>
            Sarthe — Pays de la Loire
          </span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mb-6 leading-tight" style={{ color: "#f5f2ec" }}>
            Le Miscanthus<br />
            <em>de la Sarthe</em>
          </h1>
          <p className="text-lg sm:text-xl mb-10 max-w-2xl mx-auto leading-relaxed opacity-90"
            style={{ color: "#eae4d8", fontFamily: "'Satoshi', sans-serif" }}>
            Rhizomes, paillage et biomasse issus d'une exploitation agricole familiale engagée dans une agriculture durable.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary px-8 py-3.5 rounded-md font-body font-500 text-base inline-flex items-center justify-center gap-2" data-testid="hero-cta-contact">
              Demander un devis <ArrowRight size={18} />
            </Link>
            <Link to="/produits" className="btn-outline-light px-8 py-3.5 rounded-md font-body font-500 text-base inline-flex items-center justify-center" data-testid="hero-cta-produits">
              Nos Produits
            </Link>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce opacity-60">
          <div className="w-6 h-10 rounded-full border-2 flex items-start justify-center p-1.5" style={{ borderColor: "rgba(245,242,236,0.6)" }}>
            <div className="w-1 h-2 rounded-full" style={{ backgroundColor: "#f5f2ec" }} />
          </div>
        </div>
      </section>

      {/* Reassurance band */}
      <section data-testid="reassurance-band" className="px-4 sm:px-6 lg:px-8 py-5" style={{ backgroundColor: "#2d5016" }}>
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10">
          {[
            { Icon: Truck, label: "Livraison Sarthe & Pays de la Loire" },
            { Icon: Clock, label: "Réponse sous 48 h" },
            { Icon: BadgeCheck, label: "Devis gratuit" },
          ].map(({ Icon, label }) => (
            <div key={label} className="flex items-center gap-2.5">
              <Icon size={20} style={{ color: "#d9b382", flexShrink: 0 }} />
              <span className="text-sm font-500" style={{ color: "#f5f2ec", fontFamily: "'Satoshi', sans-serif" }}>{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Products */}
      <section data-testid="products-section" className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: "#f5f2ec" }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <div className="section-divider mb-4" />
            <h2 className="font-display text-4xl sm:text-5xl" style={{ color: "#3a2b24" }}>Nos Produits</h2>
            <p className="mt-5 text-base max-w-lg" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>
              Disponibles à la commande avec livraison possible en Sarthe et Pays de la Loire.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <Link
                key={p.id}
                to={p.href}
                className="group card-hover rounded-xl overflow-hidden"
                style={{ border: "1px solid #d8d1c4", animationDelay: `${i * 0.1}s` }}
                data-testid={`product-card-${p.id}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img src={p.img} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" style={{ objectPosition: p.imgPosition || "center" }} />
                </div>
                <div className="p-6" style={{ backgroundColor: "white" }}>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-display text-xl" style={{ color: "#3a2b24" }}>{p.name}</h3>
                  </div>
                  <p className="text-sm mb-4 leading-relaxed" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>{p.desc}</p>
                  <div className="flex items-end justify-between">
                    <span className="text-xs font-body" style={{ color: "#b57a3e", fontFamily: "'Satoshi', sans-serif" }}>Sur devis — contactez-nous</span>
                    <ArrowRight size={18} style={{ color: "#b57a3e" }} className="transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Feature: About */}
      <section data-testid="about-section" style={{ backgroundColor: "#eae4d8" }} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="section-divider mb-4" />
              <h2 className="font-display text-4xl sm:text-5xl mb-6" style={{ color: "#3a2b24" }}>
                Une exploitation familiale<br />
                <em>au cœur de la Sarthe</em>
              </h2>
              <p className="text-base leading-relaxed mb-4" style={{ color: "#3a2b24", opacity: 0.8, fontFamily: "'Satoshi', sans-serif" }}>
                L'EARL Torcé Biomasse Energie cultive le miscanthus depuis plusieurs années dans la région sarthoise. Nous nous engageons pour une agriculture durable, sans pesticides, qui valorise les ressources locales.
              </p>
              <p className="text-base leading-relaxed mb-8" style={{ color: "#3a2b24", opacity: 0.8, fontFamily: "'Satoshi', sans-serif" }}>
                Nos rhizomes et notre paille de miscanthus sont produits dans le respect de l'environnement et des pratiques agroécologiques.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/exploitation" className="btn-primary px-6 py-3 rounded-md font-body font-500 text-sm inline-flex items-center gap-2" data-testid="about-cta">
                  Notre histoire <ArrowRight size={16} />
                </Link>
                <Link to="/le-miscanthus" className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-body font-500 transition-colors hover:bg-beige" style={{ color: "#2d5016", border: "1px solid #2d5016" }}>
                  <Leaf size={16} /> En savoir plus
                </Link>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src={FARMER_IMG}
                alt="EARL Torcé Biomasse Energie - Notre exploitation"
                loading="lazy"
                decoding="async"
                className="rounded-2xl w-full object-cover shadow-lg"
                style={{ aspectRatio: "4/3", maxHeight: "480px" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Seasonal calendar */}
      <section data-testid="calendar-section" className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: "#f5f2ec" }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <div className="section-divider mb-4" />
            <h2 className="font-display text-4xl sm:text-5xl" style={{ color: "#3a2b24" }}>Disponibilité Saisonnière</h2>
            <p className="mt-5 text-sm" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>
              Quand commander nos produits ?
            </p>
          </div>
          <div className="rounded-xl p-6 sm:p-8" style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }}>
            {Object.entries({
              "Rhizomes": seasonality.rhizomes,
              "Paille (vrac & sachets)": seasonality.paille,
            }).map(([label, avail]) => (
              <div key={label} className="mb-6 last:mb-0" data-testid={`calendar-row-${label}`}>
                <p className="text-sm font-body mb-3" style={{ color: "#3a2b24", fontWeight: 600 }}>{label}</p>
                <div className="grid grid-cols-12 gap-1">
                  {months.map((m, i) => (
                    <div key={m} className="flex flex-col items-center gap-1">
                      <div
                        className={`w-full h-8 rounded-md transition-all ${
                          avail[i] ? "month-available" : "month-unavailable"
                        }`}
                        title={`${monthsFull[i]} — ${avail[i] ? "Disponible" : "Indisponible"}`}
                      />
                      {/* Taille fluide : sur les tres petits ecrans les cases font ~16px,
                          la police se reduit pour que les libelles ne se chevauchent pas. */}
                      <span className="font-body" style={{ color: "#3a2b24", opacity: 0.6, fontSize: "clamp(7px, 2.2vw, 10px)" }}>{m}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex items-center gap-6 mt-4 pt-4" style={{ borderTop: "1px solid #d8d1c4" }}>
              <div className="flex items-center gap-2 text-xs" style={{ fontFamily: "'Satoshi', sans-serif", color: "#3a2b24", opacity: 0.7 }}>
                <div className="w-4 h-4 rounded month-available" />
                Disponible
              </div>
              <div className="flex items-center gap-2 text-xs" style={{ fontFamily: "'Satoshi', sans-serif", color: "#3a2b24", opacity: 0.7 }}>
                <div className="w-4 h-4 rounded month-unavailable" />
                Indisponible
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rhizomes CTA */}
      <section data-testid="calculator-cta" className="py-16 px-4 sm:px-6" style={{ backgroundColor: "#eae4d8" }}>
        <div className="max-w-3xl mx-auto text-center">
          <Leaf size={36} style={{ color: "#b57a3e" }} className="mx-auto mb-4" />
          <h2 className="font-display text-3xl sm:text-4xl mb-4" style={{ color: "#3a2b24" }}>
            Un projet de plantation de miscanthus ?
          </h2>
          <p className="text-sm mb-6 max-w-xl mx-auto" style={{ color: "#3a2b24", opacity: 0.7, fontFamily: "'Satoshi', sans-serif" }}>
            Pour vous proposer le meilleur conseil, indiquez-nous la <strong>surface que vous souhaitez planter</strong>, <strong>l'utilisation envisagée</strong> (paillage, haie brise-vent, biomasse, énergie…) ainsi que votre <strong>type de sol</strong>. Nous reviendrons vers vous avec une recommandation personnalisée.
          </p>
          <Link to="/contact" className="btn-primary px-8 py-3.5 rounded-md font-body font-500 text-base inline-flex items-center gap-2" data-testid="calculator-cta-link">
            Nous contacter <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section data-testid="testimonials-section" id="temoignages" className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: "#f5f2ec" }}>
          <div className="max-w-7xl mx-auto">
            <div className="mb-12">
              <div className="section-divider mb-4" />
              <h2 className="font-display text-4xl sm:text-5xl" style={{ color: "#3a2b24" }}>Ils nous font confiance</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((t, i) => (
                <div
                  key={t.id || i}
                  className="p-6 rounded-xl"
                  style={{ backgroundColor: "white", border: "1px solid #d8d1c4" }}
                  data-testid={`testimonial-${i}`}
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={14} className={s < (t.rating || 5) ? "star-filled" : "star-empty"} fill={s < (t.rating || 5) ? "#b57a3e" : "none"} />
                    ))}
                  </div>
                  <p className="text-sm leading-relaxed mb-6 italic" style={{ color: "#3a2b24", opacity: 0.8, fontFamily: "'Satoshi', sans-serif" }}>
                    "{t.content}"
                  </p>
                  <div>
                    <p className="font-body font-600 text-sm" style={{ color: "#3a2b24" }}>{t.author}</p>
                    <p className="text-xs" style={{ color: "#b57a3e", fontFamily: "'Satoshi', sans-serif" }}>{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section data-testid="final-cta" style={{ backgroundColor: "#2d5016" }} className="py-20 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-4xl sm:text-5xl mb-6" style={{ color: "#f5f2ec" }}>
            Prêt à cultiver le miscanthus ?
          </h2>
          <p className="text-base mb-8 opacity-80" style={{ color: "#eae4d8", fontFamily: "'Satoshi', sans-serif" }}>
            Demandez un devis personnalisé ou contactez-nous pour plus d'informations sur nos produits.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-md font-body font-500 text-base transition-all hover:-translate-y-0.5" style={{ backgroundColor: "#b57a3e", color: "white" }} data-testid="final-cta-link">
            Nous contacter <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
}
